/**
 * Booking tool DEFINITIONS — pure data, no browser dependencies.
 *
 * This is the single source of truth for each tool's name, description, and
 * input schema. It is intentionally dependency-free so it can be imported by:
 *
 * - the Cloudflare Worker (Track B), whose active provider maps them to that
 *   provider's tool format, and
 * - the in-widget registry (`helpers/tools.svelte.js`), which attaches the
 *   client-side `execute` functions to these definitions, and
 * - the on-device Prompt API driver (Track C).
 *
 * The `execute` functions live in `helpers/tools.svelte.js` (they need browser
 * context). Keep this file free of any import so every consumer — including the
 * Worker runtime — can use it.
 */

export const toolDefinitions = [
    {
        name: 'list_treatments',
        description: "List the salon's bookable treatments (name, duration in minutes, and a price only where the salon publishes it online — a treatment without a price has none to give). Optionally pass an employee (name or id) to list only treatments they perform.",
        annotations: { readOnlyHint: true },
        inputSchema: {
            type: 'object',
            properties: {
                employee: { type: 'string', description: 'Optional employee name or id to filter treatments.' }
            }
        }
    },
    {
        name: 'list_employees',
        description: "List the salon's bookable employees. Optionally pass a treatment (name or id) to filter to those who perform it. Also reports whether a \"no preference\" option exists.",
        annotations: { readOnlyHint: true },
        inputSchema: {
            type: 'object',
            properties: {
                treatment: { type: 'string', description: 'Optional treatment name or id to filter employees.' }
            }
        }
    },
    {
        name: 'get_availability',
        description: "All available dates for a treatment (and optional employee) across the whole booking horizon — a date not in the list is not available; if a date is given, returns that date's time slots instead. Call it BEFORE offering any date or time to the customer; never offer to call it.",
        annotations: { readOnlyHint: true },
        inputSchema: {
            type: 'object',
            properties: {
                treatment: { type: 'string', description: 'Treatment name or id.' },
                employee: { type: 'string', description: 'Optional employee name or id; omit for no preference.' },
                date: { type: 'string', description: "Optional ISO date (YYYY-MM-DD); returns that date's times." }
            },
            required: ['treatment']
        }
    },
    {
        name: 'get_salon_info',
        description: "The salon's contact details (name, phone, email, address, website) — to answer questions about the salon itself.",
        annotations: { readOnlyHint: true },
        inputSchema: {
            type: 'object',
            properties: {}
        }
    },
    {
        name: 'get_customer',
        description: "What's known about this returning visitor: whether their details are saved, their name, which required fields are missing, whether booking can proceed (readyToBook), and their previously chosen treatment. Call BEFORE asking for details — returning customers usually needn't re-enter them. Contact values (email/phone) aren't returned; stage_appointment reuses stored values automatically.",
        annotations: { readOnlyHint: true },
        inputSchema: {
            type: 'object',
            properties: {}
        }
    },
    {
        name: 'stage_appointment',
        description: 'Stage a complete booking in one call: resolves treatment + employee, validates the date/time against real availability, and checks the customer. The "customer" arg is OPTIONAL for returning customers (known details are reused; supply fields only to add or override). Does NOT finalize — the customer must press the confirm button themselves; until they do, call it again with different details whenever they change their mind (the new card replaces the old one). If the salon requires identity verification, call submit_verification_code before booking continues. On invalid input the response lists the valid options to retry.',
        annotations: { readOnlyHint: false },
        inputSchema: {
            type: 'object',
            properties: {
                treatment: { type: 'string', description: 'Treatment name or id.' },
                employee: { type: 'string', description: 'Optional employee name or id; omit for no preference.' },
                date: { type: 'string', description: 'ISO date (YYYY-MM-DD), an available date.' },
                time: { type: 'string', description: 'Start time (HH:MM), an available slot for that date.' },
                customer: {
                    type: 'object',
                    description: 'Contact details. Optional for returning customers; provide fields only to add missing ones or override.',
                    properties: {
                        firstname: { type: 'string' },
                        lastname: { type: 'string' },
                        email: { type: 'string', description: 'Valid email address.' },
                        telephone: { type: 'string', description: 'Phone number; any national/international format.' }
                    }
                }
            },
            required: ['treatment', 'date', 'time']
        }
    },
    {
        name: 'submit_verification_code',
        description: 'Submit the verification code the customer received (email or SMS) after stage_appointment reported verification is required.',
        annotations: { readOnlyHint: false },
        inputSchema: {
            type: 'object',
            properties: {
                code: { type: 'string', description: 'The code the customer received (typically 4 digits).' }
            },
            required: ['code']
        }
    }
];

// Provider-neutral by design: `name`, `description`, `inputSchema` (JSON Schema), and
// `annotations`. Each provider module (worker/providers/) maps these to its own tool
// shape; the client attaches `execute` (helpers/tools.svelte.js). Nothing here is
// Anthropic-specific.
